import { calculateRect, calculateLayout, getBreakpointForWidth, measureNode } from '../utils.js';
import { resolveNode } from '../utils/responsive.js';

export class HierarchyManager {
  static moveNodes(dragIds, targetId, position, activeNodes, rootW, rootH, activeBreakpoint) {
    const uniqueDragIds = [...new Set(dragIds)];

    // Prevent dropping on self
    if (targetId && uniqueDragIds.includes(targetId)) {
      return null;
    }

    // Prevent dropping on descendant
    let ancestorId = targetId;
    while (ancestorId) {
      if (uniqueDragIds.includes(ancestorId)) return null;
      ancestorId = activeNodes.find(n => n.id === ancestorId)?.parentId || null;
    }

    const nodesToMove = activeNodes.filter(n => uniqueDragIds.includes(n.id));
    if (nodesToMove.length === 0) return null;

    const effectiveBreakpoint = activeBreakpoint || getBreakpointForWidth(rootW);

    // Helper to convert absolute position to relative position under new parent
    const convertToRelativePosition = (absRect, newParentId) => {
      let parentW = rootW;
      let parentH = rootH;
      let parentOffsetX = 0;
      let parentOffsetY = 0;
      
      // Offsets for Border and Scroll
      let borderLeft = 0;
      let borderTop = 0;
      let scrollLeft = 0;
      let scrollTop = 0;

      if (newParentId) {
        // Use activeNodes to find the parent (before modifications)
        const parentNode = activeNodes.find(n => n.id === newParentId);
        if (parentNode) {
          // Use measureNode to get accurate visual rect of parent (Canvas Space)
          const parentAbsRect = measureNode(newParentId, activeNodes, rootW, rootH, effectiveBreakpoint);
          
          parentW = parentAbsRect.width;
          parentH = parentAbsRect.height;
          parentOffsetX = parentAbsRect.x;
          parentOffsetY = parentAbsRect.y;

          // Get DOM element for precise border/scroll correction
          if (typeof document !== 'undefined') {
            const parentEl = document.getElementById(newParentId);
            if (parentEl) {
               borderLeft = parentEl.clientLeft || 0;
               borderTop = parentEl.clientTop || 0;
               scrollLeft = parentEl.scrollLeft || 0;
               scrollTop = parentEl.scrollTop || 0;
            }
          }
        }
      }

      // Calculate relative position within new parent
      // Visual Pos = Parent Visual + Border + Relative - Scroll
      // Relative = Visual Pos - Parent Visual - Border + Scroll
      const relativeRect = {
        x: absRect.x - parentOffsetX - borderLeft + scrollLeft,
        y: absRect.y - parentOffsetY - borderTop + scrollTop,
        width: absRect.width,
        height: absRect.height
      };

      // Use default anchors (top-left corner)
      const anchors = { minX: 0, maxX: 0, minY: 0, maxY: 0 };
      return calculateLayout(relativeRect, anchors, parentW, parentH);
    };

    const nodesToKeep = activeNodes.filter(n => !uniqueDragIds.includes(n.id));
    let newNodes = [...nodesToKeep];
    let newParentId;

    // Calculate absolute positions for all nodes to move BEFORE modifying the tree
    // Use measureNode to capture exact visual state (including transforms/borders from previous state)
    const nodeAbsolutePositions = new Map();
    nodesToMove.forEach(node => {
      nodeAbsolutePositions.set(node.id, measureNode(node.id, activeNodes, rootW, rootH, effectiveBreakpoint));
    });

    const createUpdatedNode = (n, newParentId, newLayout) => {
      // Clean up layout overrides to ensure new base values take effect
      const newProps = { ...n.props };
      if (newProps.responsiveOverrides) {
        const newOverrides = { ...newProps.responsiveOverrides };
        Object.keys(newOverrides).forEach(bp => {
          const bpOver = { ...newOverrides[bp] };
          // Remove layout properties
          delete bpOver.layout_x;
          delete bpOver.layout_y;
          delete bpOver.layout_width;
          delete bpOver.layout_height;
          delete bpOver.layout_anchors;

          if (Object.keys(bpOver).length > 0) {
            newOverrides[bp] = bpOver;
          } else {
            delete newOverrides[bp];
          }
        });
        newProps.responsiveOverrides = newOverrides;
      }

      return {
        ...n,
        parentId: newParentId,
        x: newLayout.x,
        y: newLayout.y,
        width: newLayout.width,
        height: newLayout.height,
        anchors: { minX: 0, maxX: 0, minY: 0, maxY: 0 },
        props: newProps
      };
    };

    if (!targetId) {
      // Drop at root
      newParentId = undefined;
      const updatedNodes = nodesToMove.map(n => {
        const absRect = nodeAbsolutePositions.get(n.id);
        const newLayout = convertToRelativePosition(absRect, newParentId);
        return createUpdatedNode(n, newParentId, newLayout);
      });
      newNodes.push(...updatedNodes);
    } else {
      const targetNode = activeNodes.find(n => n.id === targetId);
      if (!targetNode) return null;

      if (position === 'inside') {
        newParentId = targetId;
        const updatedNodes = nodesToMove.map(n => {
          const absRect = nodeAbsolutePositions.get(n.id);
          const newLayout = convertToRelativePosition(absRect, newParentId);
          return createUpdatedNode(n, newParentId, newLayout);
        });
        newNodes.push(...updatedNodes);
      } else {
        newParentId = targetNode.parentId;
        const targetIndex = newNodes.findIndex(n => n.id === targetId);

        const updatedNodes = nodesToMove.map(n => {
          const absRect = nodeAbsolutePositions.get(n.id);
          const newLayout = convertToRelativePosition(absRect, newParentId);
          return createUpdatedNode(n, newParentId, newLayout);
        });

        if (targetIndex !== -1) {
          const insertIndex = position === 'above' ? targetIndex : targetIndex + 1;
          newNodes.splice(insertIndex, 0, ...updatedNodes);
        } else {
          newNodes.push(...updatedNodes);
        }
      }
    }

    return newNodes;
  }

  static deleteNodes(nodeIds, activeNodes) {
    const deleteRecursive = (targetIds, list) => {
      let newList = list.filter(n => !targetIds.includes(n.id));
      targetIds.forEach(tid => {
        const children = list.filter(n => n.parentId === tid);
        if (children.length > 0) {
          newList = deleteRecursive(children.map(c => c.id), newList);
        }
      });
      return newList;
    };

    return deleteRecursive(nodeIds, activeNodes);
  }

  static duplicateNodes(nodeIds, activeNodes, deepCloneNodes) {
    const roots = nodeIds.filter(id => {
      const n = activeNodes.find(x => x.id === id);
      if (!n) return false;
      let p = activeNodes.find(x => x.id === n.parentId);
      while (p) {
        if (nodeIds.includes(p.id)) return false;
        p = activeNodes.find(x => x.id === p.parentId);
      }
      return true;
    });

    if (roots.length === 0) return null;

    let newActiveNodes = [...activeNodes];
    const newSelection = [];
    const targets = roots
      .map(id => ({ id, index: activeNodes.findIndex(n => n.id === id) }))
      .sort((a, b) => b.index - a.index);

    targets.forEach(({ id, index }) => {
      const result = deepCloneNodes(activeNodes, id);
      if (result) {
        const { newNodes, newRootId } = result;
        const newRoot = newNodes.find(n => n.id === newRootId);
        const original = activeNodes[index];

        if (newRoot) {
          newRoot.x += 20;
          newRoot.y += 20;
        }

        newActiveNodes.splice(index + 1, 0, ...newNodes);
        newSelection.push(newRootId);
      }
    });

    return { newNodes: newActiveNodes, newSelection };
  }
}
